<template>
  <Teleport :to="teleportTarget">
    <div
      class="absolute z-80000 pointer-events-none w-full h-full inset-0"
    >
      <transition name="fade" appear>
        <div
          v-if="active"
          key="dialog-overlay"
          :class="overlayClass"
          @click="handleClick()"
        />
      </transition>
      <transition name="pop" appear>
        <div
          v-if="active"
          key="dialog-container"
          class="absolute m-auto w-full h-full z-80000 flex items-center justify-center pointer-events-none"
          role="dialog"
        >
          <div class="relative m-2 pointer-events-auto w-full sm:max-w-md sm:w-full">
            <o-scroll base-class="h-full w-full max-w-full max-h-[95svh] border rounded-xl bg-white dark:bg-gray-900 dark:border-gray-800 shadow-sm">
              <div class="pt-5 pb-4 sm:px-6 sm:pb-6">
                <div>
                  <slot name="heading">
                    <div v-if="icon" class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md opacity-75 mb-4 bg-gray-100 dark:bg-gray-500">
                      <o-icon :icon="icon" :size="22" />
                    </div>
                  </slot>
                  <div class="mt-3">
                    <slot name="title">
                      <h3 id="modal-headline" class="font-bold text-xl text-gray-700 mb-2 dark:text-gray-200 leading-4 tracking-tight">
                        {{ title }}
                      </h3>
                    </slot>
                    <slot name="info">
                      <p v-if="info" class="mb-1 text-sm leading-5 text-gray-600 dark:text-gray-300" v-html="info" />
                    </slot>
                    <div v-if="$slots.default" :class="slotClass">
                      <slot :active="active" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="px-3 py-3 sm:px-6 sm:pb-6 sm:flex sm:flex-col space-y-2">
                <slot name="buttons">
                  <o-button
                    v-for="(button, index) in buttons"
                    :key="index"
                    class="w-full h-9"
                    :variant="button.variant"
                    :flat="button.flat"
                    :outline="button.outline"
                    :disabled="button.disabled || isLoading"
                    :icon="button.icon"
                    @click="handleClick(button)"
                  >
                    {{ button.text }}
                  </o-button>
                </slot>
              </div>
            </o-scroll>

            <transition name="fade">
              <div v-if="isLoading" class="z-20 absolute w-full h-full inset-0 rounded-lg bg-gray-100 p-6 text-gray-800 text-sm font-medium flex flex-col items-center justify-center dark:bg-gray-900 dark:text-gray-100">
                <slot name="loading">
                  <o-loader
                    :scale="0.4"
                    class="h-10 w-10 mb-4"
                  />
                  <p>{{ $t('general.loading') }}</p>
                </slot>
              </div>
            </transition>
          </div>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<script lang="ts">
export default { name: 'ODialog' }
</script>

<script setup lang="ts">
const { $i18n } = useNuxtApp()

interface Props {
  options?: any[]
  info?: string | null
  title?: string
  icon?: string | null
  loading?: boolean
  portal?: string | null
  container?: string | object | null
  valid?: Function
  overlayClass?: string
  slotClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  info: null,
  title: undefined,
  icon: null,
  loading: false,
  portal: 'dialog-container',
  container: null,
  valid: () => () => true,
  overlayClass: 'absolute inset-0 w-full h-full bg-black opacity-50 rounded-md z-80000 transition pointer-events-auto',
  slotClass: 'py-2',
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'confirm'): void
  (e: 'cancel'): void
  (e: string): void
}>()

const title = computed(() => props.title ?? $i18n.t('general.info'))
const teleportTarget = computed(() => {
  const target = props.portal?.trim()
  if (!target) {
    return '#dialog-container'
  }

  return target.startsWith('#') ? target : `#${target}`
})

const active = ref(false)
const internalLoading = ref(false)
const isLoading = computed(() => props.loading || internalLoading.value)
const resolveRef = ref<((value: any) => void) | null>(null)
const rejectRef = ref<((reason?: any) => void) | null>(null)

const defaults: Record<string, any> = {
  confirm: {
    text: $i18n.t('general.confirm'),
    variant: 'primary',
    outline: false,
    flat: false,
    response: 'confirm',
    validate: true,
  },
  cancel: {
    text: $i18n.t('general.cancel'),
    variant: undefined,
    outline: true,
    flat: true,
    response: 'cancel',
    validate: false,
  },
}

const buttons = computed(() => {
  return props.options.map((option: any) => {
    return Object.assign({ ...defaults[option.type] }, option)
  })
})

async function handleClick (options: any = { response: 'cancel' }) {
  if (isLoading.value) {
    return
  }

  internalLoading.value = true

  try {
    const isValid = !options.validate || await props.valid()

    if (isValid) {
      emit(options.response)

      const callback = options.callback

      if (callback) {
        const success = await callback()

        if (success) {
          close()
          resolveRef.value?.(options.response)
        }
      } else {
        close()
        resolveRef.value?.(options.response)
      }
    }
  } finally {
    internalLoading.value = false
  }
}

function open () {
  active.value = true

  return new Promise((resolve, reject) => {
    resolveRef.value = resolve
    rejectRef.value = reject
  })
}

function close () {
  emit('close')
  active.value = false
}

defineExpose({ open, close })
</script>

<style>
.pop-enter-active,
.pop-leave-active {
  transition: transform 0.3s, opacity 0.3s
}

.pop-enter-from,
.pop-leave-to {
  opacity: 0;
  transform: scale(0.3);
}
</style>
